import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import { useAuth } from './AuthProvider';

// Default background: dark geometric gradient that conveys structure, strategy, and precision.
// Tenants can override this via their branding settings (loginBackgroundUrl).
const defaultBackground = `
  radial-gradient(ellipse at 20% 50%, rgba(124, 58, 237, 0.15) 0%, transparent 50%),
  radial-gradient(ellipse at 80% 20%, rgba(37, 99, 235, 0.12) 0%, transparent 50%),
  radial-gradient(ellipse at 60% 80%, rgba(13, 148, 136, 0.08) 0%, transparent 50%),
  linear-gradient(180deg, #060912 0%, #0A0E17 50%, #0D1220 100%)
`;

// Subtle grid pattern overlay
const gridOverlay = `
  linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
  linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
`;

export default function LoginPage() {
  const { login, verifyMfa, requiresMfa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaCode);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{
        background: defaultBackground,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: gridOverlay,
          backgroundSize: '60px 60px',
          opacity: 1,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Floating accent orb — top right */}
      <Box sx={{
        position: 'absolute', top: -120, right: -80,
        width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.08) 0%, transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      {/* Floating accent orb — bottom left */}
      <Box sx={{
        position: 'absolute', bottom: -100, left: -60,
        width: 350, height: 350, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)',
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      {/* Login card */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 420,
          width: '100%',
          mx: 2,
          p: 5,
          borderRadius: 3,
          background: 'rgba(18, 24, 41, 0.85)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 80px rgba(124,58,237,0.05)',
        }}
      >
        {/* Logo & branding */}
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700, color: '#fff',
          }}>
            C
          </Box>
          <Typography variant="h5" fontWeight={700} sx={{
            background: 'linear-gradient(135deg, #E8ECF4 0%, #8892A8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}>
            Cadence
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={4} sx={{ fontSize: 13, letterSpacing: '0.02em' }}>
          Executive Orchestration Platform
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {!requiresMfa ? (
          <form onSubmit={handleLogin}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              sx={{ mb: 2.5 }}
              autoFocus
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 2, background: 'rgba(255,255,255,0.03)' }
              }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              sx={{ mb: 3.5 }}
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 2, background: 'rgba(255,255,255,0.03)' }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleMfa}>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Enter the 6-digit code from your authenticator app.
            </Typography>
            <TextField
              label="MFA Code"
              fullWidth
              required
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
              inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
              sx={{ mb: 3.5 }}
              autoFocus
              variant="outlined"
              InputProps={{
                sx: { borderRadius: 2, background: 'rgba(255,255,255,0.03)', letterSpacing: '0.3em', textAlign: 'center' }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: 15,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #7C3AED 0%, #6C3ACD 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #6D28D9 0%, #5B21B6 100%)' },
                boxShadow: '0 4px 14px rgba(124,58,237,0.3)',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Verify'}
            </Button>
          </form>
        )}

        {/* OAuth divider */}
        {!requiresMfa && (
          <>
            <Box display="flex" alignItems="center" my={3} gap={2}>
              <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
              <Typography variant="body2" sx={{ fontSize: 12, color: 'rgba(136,146,168,0.6)' }}>or</Typography>
              <Box flex={1} height="1px" bgcolor="rgba(255,255,255,0.08)" />
            </Box>

            {/* OAuth buttons */}
            <Button
              fullWidth
              variant="outlined"
              href={`${process.env.REACT_APP_API_URL || 'https://api.stratiumhq.com/api'}/auth/login/microsoft`}
              sx={{
                mb: 1.5, py: 1.2, borderRadius: 2, fontSize: 14, fontWeight: 500,
                borderColor: 'rgba(255,255,255,0.12)', color: '#E8ECF4',
                '&:hover': { borderColor: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.04)' },
                textTransform: 'none',
              }}
            >
              Sign in with Microsoft 365
            </Button>

            <Button
              fullWidth
              variant="outlined"
              href={`${process.env.REACT_APP_API_URL || 'https://api.stratiumhq.com/api'}/auth/login/google`}
              sx={{
                py: 1.2, borderRadius: 2, fontSize: 14, fontWeight: 500,
                borderColor: 'rgba(255,255,255,0.12)', color: '#E8ECF4',
                '&:hover': { borderColor: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.04)' },
                textTransform: 'none',
              }}
            >
              Sign in with Google
            </Button>
          </>
        )}

        {/* Footer links */}
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            Don't have an account?{' '}
            <Link href="/signup" underline="hover" sx={{ color: '#7C3AED', fontWeight: 600 }}>
              Start free trial
            </Link>
          </Typography>
        </Box>

        {/* Stratium footer */}
        <Box mt={3} textAlign="center">
          <Typography variant="body2" sx={{ fontSize: 11, color: 'rgba(136,146,168,0.5)' }}>
            Powered by Stratium
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
